/** @jsxImportSource react */
import { useState } from 'react'
import {
  Box, Button, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { recordsAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { useEntityAccess } from '../../hooks/usePermissions'
import { allFields, formatValue, initialValues, toInputValues, toRequestData } from './fieldValues'
import FieldInput from './FieldInput'
import { LookupLink } from './LookupInput'
import ConfirmDialog from '../common/ConfirmDialog'
import { useNotify } from '../common/NotificationProvider'
import { entityLabel, entityLabelPlural } from '../../utils/helpers'
import { getErrorList, getErrorMessage, toFieldErrors } from '../../utils/apiErrors'

const MAX_COLUMNS = 7
const SUMMABLE = ['NUMBER', 'DECIMAL', 'CURRENCY', 'ROLLUP']

const Cell = ({ field, record }) => {
  const value = record.data[field.fieldKey]
  if (field.fieldType === 'LOOKUP' || field.fieldType === 'USER') return <LookupLink refValue={record.lookups?.[field.fieldKey]} />
  return formatValue(field, value)
}

/**
 * Child records edited in place on the parent's page (quote → line items). Each row saves on its
 * own (✓), so the parent's form doesn't need saving. Totals belong in the parent's roll-up fields,
 * which the server updates; a column sum here would also add up things like unit prices.
 */
const LineItemsTable = ({ group, parent, childForm }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const notify = useNotify()
  const access = useEntityAccess(group.entityType)
  const [editing, setEditing] = useState(null) // { id | 'new', values, errors }
  const [busy, setBusy] = useState(false)
  const [deleting, setDeleting] = useState(null)

  const fields = allFields(childForm)
  const columns = fields.filter((f) => f.fieldKey !== group.fieldKey && !f.hidden && f.fieldType !== 'TEXTAREA').slice(0, MAX_COLUMNS)
  const refresh = () => queryClient.invalidateQueries({ queryKey: [tenantId, 'records'] })

  const start = (record) => setEditing(record
    ? { id: record.id, record, values: toInputValues(childForm, record.data), errors: {} }
    : { id: 'new', values: { ...initialValues(childForm), [group.fieldKey]: parent.id }, errors: {} })

  const save = async () => {
    setBusy(true)
    try {
      const data = { ...toRequestData(childForm, editing.values), [group.fieldKey]: parent.id }
      if (editing.id === 'new') await recordsAPI.create({ formCode: childForm.code, data })
      else await recordsAPI.update(editing.id, { data, lockVersion: editing.record.lockVersion })
      setEditing(null)
      refresh()
    } catch (e) {
      const errors = toFieldErrors(e)
      setEditing((prev) => ({ ...prev, errors }))
      const shown = new Set(columns.map((c) => c.fieldKey))
      const other = getErrorList(e).filter((m) => !shown.has(m.split(':')[0]))
      if (e?.response?.status === 409) notify('Someone else changed this row; it was reloaded.', 'error')
      else if (!Object.keys(errors).length || other.length) notify([getErrorMessage(e), ...other].join(' · '), 'error')
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    setBusy(true)
    try {
      await recordsAPI.remove(deleting.id)
      setDeleting(null)
      refresh()
    } catch (e) {
      notify(getErrorMessage(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  const editRow = (key) => (
    <TableRow key={key} selected>
      {columns.map((f) => (
        <TableCell key={f.fieldKey} sx={{ minWidth: 140, verticalAlign: 'top' }}>
          {f.fieldType === 'ROLLUP' ? '—' : (
            <FieldInput field={{ ...f, label: '', helpText: '' }} value={editing.values[f.fieldKey]} error={editing.errors[f.fieldKey]}
              onChange={(v) => setEditing((prev) => ({ ...prev, values: { ...prev.values, [f.fieldKey]: v }, errors: { ...prev.errors, [f.fieldKey]: undefined } }))}
              disabled={busy} lookupRef={editing.record?.lookups?.[f.fieldKey]} />
          )}
        </TableCell>
      ))}
      <TableCell align="right" sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
        <Tooltip title="Save row"><span><IconButton color="primary" onClick={save} disabled={busy} aria-label="Save row"><CheckIcon /></IconButton></span></Tooltip>
        <Tooltip title="Cancel"><IconButton onClick={() => setEditing(null)} disabled={busy} aria-label="Cancel"><CloseIcon /></IconButton></Tooltip>
      </TableCell>
    </TableRow>
  )


  return (
    <Paper variant="outlined">
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.25, gap: 1 }}>
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          {entityLabelPlural(group.entityType)}
          <Typography component="span" variant="body2" color="text.secondary"> · {group.total}</Typography>
        </Typography>
        {access.canCreate && (
          <Button startIcon={<AddIcon />} onClick={() => start(null)} disabled={!!editing}>Add {entityLabel(group.entityType).toLowerCase()}</Button>
        )}
      </Box>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((f) => <TableCell key={f.fieldKey} align={SUMMABLE.includes(f.fieldType) ? 'right' : 'left'}>{f.label}</TableCell>)}
              <TableCell sx={{ width: 120 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {group.items.map((r) => (editing?.id === r.id ? editRow(r.id) : (
              <TableRow key={r.id} hover>
                {columns.map((f) => (
                  <TableCell key={f.fieldKey} align={SUMMABLE.includes(f.fieldType) ? 'right' : 'left'}><Cell field={f} record={r} /></TableCell>
                ))}
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {access.canEdit(r, fields) && (
                    <Tooltip title="Edit row"><span><IconButton onClick={() => start(r)} disabled={!!editing} aria-label="Edit row"><EditOutlinedIcon fontSize="small" /></IconButton></span></Tooltip>
                  )}
                  <Tooltip title="Open"><IconButton onClick={() => navigate(`/records/${r.entityType}/${r.id}`)} aria-label="Open"><OpenInNewIcon fontSize="small" /></IconButton></Tooltip>
                  {access.canDelete(r, fields) && (
                    <Tooltip title="Delete row"><span><IconButton onClick={() => setDeleting(r)} disabled={!!editing} aria-label="Delete row"><DeleteOutlineIcon fontSize="small" /></IconButton></span></Tooltip>
                  )}
                </TableCell>
              </TableRow>
            )))}
            {editing?.id === 'new' && editRow('new')}
            {group.items.length === 0 && editing?.id !== 'new' && (
              <TableRow>
                <TableCell colSpan={columns.length + 1} sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}>
                  No {entityLabelPlural(group.entityType).toLowerCase()} yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {group.total > group.items.length && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1 }}>
          Showing the first {group.items.length} of {group.total}.
        </Typography>
      )}
      <ConfirmDialog
        open={!!deleting}
        title={`Delete ${entityLabel(group.entityType).toLowerCase()}?`}
        message={deleting ? `"${deleting.title}" will be deleted.` : ''}
        confirmLabel="Delete"
        destructive
        busy={busy}
        onConfirm={remove}
        onClose={() => setDeleting(null)}
      />
    </Paper>
  )
}

export default LineItemsTable
